import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import WhatsAppButton from '../components/WhatsAppButton';
import { AuthContext } from '../context/AuthContext';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';
import api from '../utils/api';

const BookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { formatPrice, language } = useLanguageCurrency();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancellationSettings, setCancellationSettings] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { redirectTo: `/client/booking/${id}` } });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      api.get(`/api/bookings/${id}`),
      api.get('/api/bookings/early-checkout-settings').catch(() => ({ data: {} }))
    ]).then(([bookRes, settingsRes]) => {
        if (!cancelled && bookRes.data?.reservation) {
          setReservation(bookRes.data.reservation);
        }
        if (!cancelled && settingsRes.data?.cancellationPolicy) {
          setCancellationSettings(settingsRes.data.cancellationPolicy);
        } else if (!cancelled && settingsRes.data) {
          setCancellationSettings(settingsRes.data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || (language === 'ar' ? 'تعذر تحميل تفاصيل الحجز' : 'Failed to load booking details'));
          setReservation(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, user, navigate, language]);

  const canCancelReservation = reservation && reservation.status === 'Confirmed';
  const getCancellationFeeBreakdown = () => {
    const totalAmount = reservation?.pricing?.totalAmount ?? 0;
    const policy = cancellationSettings;
    if (!policy) return { fee: 0, refund: totalAmount };
    const feeType = policy.feeType || 'percentage';
    const feeValue = Number(policy.feeValue) || 0;
    let fee = 0;
    if (feeType === 'percentage') {
      fee = (totalAmount * Math.min(100, Math.max(0, feeValue))) / 100;
    } else {
      fee = Math.min(totalAmount, Math.max(0, feeValue));
    }
    return { fee, refund: Math.max(0, totalAmount - fee) };
  };
  const confirmCancel = () => {
    setCancelSubmitting(true);
    api.patch(`/api/bookings/${id}/cancel`, { reason: cancelReason?.trim() || '' })
      .then((res) => {
        setReservation(res.data?.reservation || { ...reservation, status: 'Cancelled' });
        setShowCancelModal(false);
        navigate(`/client/booking/${id}`, { replace: true });
      })
      .finally(() => setCancelSubmitting(false));
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#D5B577' }} />
            <p className="text-gray-400">{language === 'ar' ? 'جاري تحميل تفاصيل الحجز...' : 'Loading booking details...'}</p>
          </div>
        </div>
        <WhatsAppButton phoneNumber="+1234567890" message="Hello! I have a question about my booking." />
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
        <Navbar />
        <div className="pt-32 pb-12 px-4 max-w-2xl mx-auto">
          <div className="rounded-2xl p-6 border bg-slate-800/50" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
            <p className="text-red-300 mb-4">{error || (language === 'ar' ? 'الحجز غير موجود' : 'Booking not found')}</p>
            <button
              onClick={() => navigate('/client')}
              className="px-4 py-2 rounded-xl font-medium transition-colors"
              style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
            >
              {language === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to dashboard'}
            </button>
          </div>
        </div>
        <WhatsAppButton phoneNumber="+1234567890" message="Hello! I have a question about my booking." />
      </div>
    );
  }

  const prop = reservation.property || {};
  const title = typeof prop.title === 'string' ? prop.title : (prop.title?.en || prop.title?.ar || 'Property');
  const address = typeof prop.address === 'string' ? prop.address : (prop.address?.en || prop.address?.ar || '');
  const city = typeof prop.city === 'string' ? prop.city : (prop.city?.en || prop.city?.ar || '');
  const loc = [address, city].filter(Boolean).join(', ');
  const checkIn = reservation.checkInDate ? new Date(reservation.checkInDate) : null;
  const checkOut = reservation.checkOutDate ? new Date(reservation.checkOutDate) : null;
  const earlyHours = reservation.earlyCheckInHours ?? 0;
  const standardHour = 14;
  const checkInHour = Math.max(0, standardHour - earlyHours);
  const checkInTimeStr = new Date(2000, 0, 1, checkInHour, 0, 0).toLocaleTimeString(
    language === 'ar' ? 'ar-EG' : 'en-US',
    { hour: 'numeric', minute: '2-digit', hour12: true }
  );
  const pricing = reservation.pricing || {};
  const locale = language === 'ar' ? 'ar-EG' : 'en-US';
  // Checkout time: default 12:00 PM, or early checkout time if same-day early checkout was set
  const hasSameDayEarlyCheckout = reservation.earlyCheckout?.type === 'sameDay' && reservation.earlyCheckout?.status === 'completed' && reservation.earlyCheckout?.requestedCheckOutTime;
  const checkOutTimeStr = hasSameDayEarlyCheckout
    ? new Date(reservation.earlyCheckout.requestedCheckOutTime).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true })
    : (language === 'ar' ? '12:00 م' : '12:00 PM');

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
      <Navbar />
      <div className="pt-28 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/client')}
          className="flex items-center gap-2 text-gray-300 hover:text-[#D5B577] transition-colors mb-6"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m7 7h18" />
          </svg>
          {language === 'ar' ? 'العودة إلى حجوزاتي' : 'Back to my bookings'}
        </button>

        <div className="rounded-2xl overflow-hidden border" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
          {/* Header */}
          <div className="p-6 sm:p-8 border-b" style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(213, 181, 119, 0.2)' }}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-mono mb-1" style={{ color: '#D5B577' }}>
                  {reservation.reservationNumber}
                </p>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">{language === 'ar' ? 'تفاصيل الحجز' : 'Booking details'}</h1>
              </div>
              <div className="flex items-center gap-3">
                {canCancelReservation && (
                  <button
                    type="button"
                    onClick={() => setShowCancelModal(true)}
                    className="px-4 py-2 rounded-xl font-medium transition-colors border flex items-center gap-2"
                    style={{ borderColor: 'rgba(239, 68, 68, 0.6)', color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {language === 'ar' ? 'إلغاء الحجز' : 'Cancel reservation'}
                  </button>
                )}
                <span
                className="px-4 py-2 rounded-xl text-sm font-bold capitalize"
                style={{
                  backgroundColor: reservation.status === 'Confirmed' ? 'rgba(76, 175, 80, 0.2)' : reservation.status === 'Pending' ? 'rgba(255, 152, 0, 0.2)' : reservation.status === 'CheckedIn' ? 'rgba(33, 150, 243, 0.2)' : reservation.status === 'CheckedOut' ? 'rgba(158, 158, 158, 0.2)' : 'rgba(158, 158, 158, 0.2)',
                  color: reservation.status === 'Confirmed' ? '#4caf50' : reservation.status === 'Pending' ? '#ff9800' : reservation.status === 'CheckedIn' ? '#2196f3' : '#9e9e9e',
                }}
              >
                {reservation.status}
              </span>
              </div>
            </div>
          </div>

          {/* Property */}
          <div className="p-6 sm:p-8 border-b" style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'rgba(213, 181, 119, 0.2)' }}>
            <h2 className="text-lg font-bold text-white mb-4">{language === 'ar' ? 'العقار' : 'Property'}</h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <img
                src={(Array.isArray(prop.images) && prop.images[0]) || prop.image || (prop.buildingImages && prop.buildingImages[0]) || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80'}
                alt={title}
                className="w-full sm:w-48 h-48 object-cover rounded-xl flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
                {loc && (
                  <p className="text-gray-400 text-sm flex items-start gap-2">
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    {loc}
                  </p>
                )}
                {prop.price != null && (
                  <p className="text-sm mt-2" style={{ color: '#D5B577' }}>
                    {formatPrice(prop.price)} {language === 'ar' ? '/ ليلة' : '/ night'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Dates & times */}
          <div className="p-6 sm:p-8 border-b" style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'rgba(213, 181, 119, 0.2)' }}>
            <h2 className="text-lg font-bold text-white mb-4">{language === 'ar' ? 'التواريخ والأوقات' : 'Dates & times'}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {checkIn && (
                <div className="p-4 rounded-xl bg-slate-800/50 border" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                  <p className="text-xs text-gray-500 mb-1">{language === 'ar' ? 'الدخول' : 'Check-in'}</p>
                  <p className="text-white font-medium">{checkIn.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  <p className="text-sm mt-1" style={{ color: '#D5B577' }}>
                    {language === 'ar' ? `من ${checkInTimeStr}` : `from ${checkInTimeStr}`}
                  </p>
                </div>
              )}
              {checkOut && (
                <div className="p-4 rounded-xl bg-slate-800/50 border" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                  <p className="text-xs text-gray-500 mb-1">{language === 'ar' ? 'الخروج' : 'Check-out'}</p>
                  <p className="text-white font-medium">{checkOut.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  <p className="text-sm mt-1" style={{ color: '#D5B577' }}>
                    {language === 'ar' ? `حتى ${checkOutTimeStr}` : `by ${checkOutTimeStr}`}
                  </p>
                </div>
              )}
            </div>
            {reservation.numberOfGuests > 0 && (
              <p className="text-gray-400 text-sm mt-4">
                {language === 'ar' ? 'الضيوف' : 'Guests'}: <span className="text-white font-medium">{reservation.numberOfGuests}</span>
              </p>
            )}
          </div>

          {/* Link to Stay details (criteria, early/late checkout, add days) – show for all statuses */}
          {(
            <div className="p-6 sm:p-8 border-b" style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'rgba(213, 181, 119, 0.2)' }}>
              <h2 className="text-lg font-bold text-white mb-2">{language === 'ar' ? 'تفاصيل الإقامة والتعديلات' : 'Stay details & modifications'}</h2>
              <p className="text-gray-400 text-sm mb-4">
                {language === 'ar'
                  ? 'عرض المعايير ووديعة التأمين، أو طلب خروج مبكر، خروج متأخر، أو إضافة أيام.'
                  : 'View criteria & insurance, or request early checkout, late checkout, or add days.'}
              </p>
              <button
                type="button"
                onClick={() => navigate(`/client/booking/${id}/stay-details`)}
                className="px-4 py-2 rounded-xl font-medium transition-colors"
                style={{ backgroundColor: 'rgba(213, 181, 119, 0.2)', color: '#D5B577', border: '1px solid rgba(213, 181, 119, 0.5)' }}
              >
                {language === 'ar' ? 'فتح صفحة تفاصيل الإقامة' : 'Open stay details page'}
              </button>
            </div>
          )}

          {/* Pricing */}
          <div className="p-6 sm:p-8" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              {language === 'ar' ? 'ملخص السعر' : 'Price summary'}
            </h2>
            <div className="bg-slate-700/30 rounded-2xl p-5 border max-w-md" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
              <div className="space-y-3">
                {/* Nights × Price */}
                {pricing.numberOfNights != null && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">
                      {pricing.numberOfNights} {language === 'ar' ? 'ليلة' : 'night(s)'} × {formatPrice(pricing.pricePerNight || 0)}
                    </span>
                    <span className="text-white font-medium">{pricing.subtotal != null ? formatPrice(pricing.subtotal) : '—'}</span>
                  </div>
                )}
                
                {/* Insurance */}
                {(pricing.insurancePrice ?? 0) > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">{language === 'ar' ? 'وديعة التأمين' : 'Insurance deposit'}</span>
                    <span className="text-white font-medium">{formatPrice(pricing.insurancePrice)}</span>
                  </div>
                )}
                
                {/* Early check-in fee */}
                {(pricing.earlyCheckInFee ?? 0) > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">{language === 'ar' ? 'رسوم الدخول المبكر' : 'Early check-in fee'}</span>
                    <span className="text-white font-medium">{formatPrice(pricing.earlyCheckInFee)}</span>
                  </div>
                )}
                
                {/* Late checkout fee */}
                {(pricing.lateCheckOutFee ?? 0) > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">{language === 'ar' ? 'رسوم الخروج المتأخر' : 'Late checkout fee'}</span>
                    <span className="text-white font-medium">{formatPrice(pricing.lateCheckOutFee)}</span>
                  </div>
                )}
                
                {/* Discount */}
                {(pricing.discountAmount ?? 0) > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-green-400">{language === 'ar' ? 'الخصم' : 'Discount'}</span>
                    <span className="text-green-400 font-medium">- {formatPrice(pricing.discountAmount)}</span>
                  </div>
                )}
                
                {/* Early checkout info */}
                {reservation.earlyCheckout?.type === 'byDays' && reservation.earlyCheckout?.status === 'completed' && (
                  <div className="border-t pt-3 mt-3" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-amber-400 text-sm font-medium">{language === 'ar' ? 'تم تطبيق الخروج المبكر' : 'Early checkout applied'}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-red-300">{language === 'ar' ? 'رسم الخروج المبكر' : 'Early checkout fee'}</span>
                      <span className="text-red-400 font-medium">- {formatPrice(reservation.earlyCheckout.feeAmount || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span className="text-green-300">{language === 'ar' ? 'مبلغ الاسترداد' : 'Refund amount'}</span>
                      <span className="text-green-400 font-medium">+ {formatPrice(reservation.earlyCheckout.refundAmount || 0)}</span>
                    </div>
                  </div>
                )}
                
                {/* Total */}
                <div className="border-t pt-3 mt-3 flex justify-between items-center" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
                  <span className="text-white font-semibold">{language === 'ar' ? 'الإجمالي' : 'Total'}</span>
                  <span className="text-xl font-bold" style={{ color: '#D5B577' }}>{pricing.totalAmount != null ? formatPrice(pricing.totalAmount) : '—'} {pricing.currency || 'EGP'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cancel reservation confirmation modal */}
        {showCancelModal && canCancelReservation && (() => {
          const { fee, refund } = getCancellationFeeBreakdown();
          const totalAmount = reservation?.pricing?.totalAmount ?? 0;
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => !cancelSubmitting && setShowCancelModal(false)}>
              <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl border" style={{ backgroundColor: '#1e293b', borderColor: 'rgba(213, 181, 119, 0.3)' }} onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {language === 'ar' ? 'إلغاء الحجز' : 'Cancel reservation'}
                  </h3>
                  <p className="text-gray-400 text-sm mt-2">
                    {language === 'ar' ? 'سيتم تطبيق رسم إلغاء على حجزك. سيتم استرداد المبلغ خلال 10–14 يوماً عمل.' : 'A cancellation fee will be applied. The refund will be processed within 10–14 working days.'}
                  </p>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {language === 'ar' ? 'سبب الإلغاء (اختياري)' : 'Reason for cancellation (optional)'}
                    </label>
                    <textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder={language === 'ar' ? 'أدخل سبب الإلغاء...' : 'Enter reason for cancellation...'}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border text-white placeholder-gray-500 focus:outline-none focus:ring-2"
                      style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}
                    />
                  </div>
                  <div className="p-4 rounded-xl bg-slate-700/30 border" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                    <p className="text-sm text-gray-300 mb-3">
                      {language === 'ar' ? 'سيتم استرداد المبلغ خلال 10–14 يوماً عمل.' : 'The refund will be processed within 10–14 working days.'}
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">{language === 'ar' ? 'المبلغ المدفوع' : 'Amount paid'}</span>
                        <span className="text-white font-medium">{formatPrice(totalAmount)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-t border-b" style={{ borderColor: 'rgba(213, 181, 119, 0.15)' }}>
                        <span className="text-red-300">{language === 'ar' ? 'رسوم الإلغاء' : 'Cancellation fee'}</span>
                        <span className="text-red-400 font-bold">- {formatPrice(fee)}</span>
                      </div>
                      <div className="flex justify-between items-center pt-1">
                        <span className="text-gray-400">{language === 'ar' ? 'المبلغ المسترد' : 'Refund amount'}</span>
                        <span className="font-bold" style={{ color: '#D5B577' }}>{formatPrice(refund)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 p-6 border-t" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                  <button
                    onClick={() => !cancelSubmitting && setShowCancelModal(false)}
                    disabled={cancelSubmitting}
                    className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all border-2"
                    style={{ borderColor: 'rgba(213, 181, 119, 0.5)', color: '#D5B577' }}
                  >
                    {language === 'ar' ? 'إلغاء' : 'Back'}
                  </button>
                  <button
                    onClick={confirmCancel}
                    disabled={cancelSubmitting}
                    className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all disabled:opacity-50"
                    style={{ backgroundColor: '#ef4444', color: 'white' }}
                  >
                    {cancelSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {language === 'ar' ? 'جاري الإلغاء...' : 'Cancelling...'}
                      </span>
                    ) : (
                      language === 'ar' ? 'تأكيد الإلغاء' : 'Confirm cancellation'
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
      <WhatsAppButton phoneNumber="+1234567890" message="Hello! I have a question about my booking." />
    </div>
  );
};

export default BookingDetails;
